import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, CircularProgress, Button, Chip } from '@mui/material';
import { WalletStyles } from '../styles';
import { scriptHistory, scriptHistoryUnc } from '../../../webservice/mProviders';
import { homenetwork, homepvtKey } from '../../../Headerr';
import { bsv } from '../../../scrypt-ts-this/distthis/index';

interface HistoryEntry {
  tx_hash: string;
  height: number;
  satoshis?: number;
}

const CACHE_TTL = 30000; // 30 seconds

const WalletHistory: FC = () => {
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const lastFetch = useRef<number>(0);
  const cachedHistory = useRef<HistoryEntry[]>([]);

  const fetchHistory = useCallback(async (force = false) => {
    if (!force && Date.now() - lastFetch.current < CACHE_TTL && cachedHistory.current.length > 0) {
      setHistory(cachedHistory.current);
      return;
    }

    if (homepvtKey.length !== 64) return;
    setLoading(true);
    try {
      const privateKey = bsv.PrivateKey.fromHex(homepvtKey, homenetwork);
      const addr = bsv.Address.fromPrivateKey(privateKey).toString();

      const confirmed = await scriptHistory(addr, homenetwork);
      const unconfirmed = await scriptHistoryUnc(addr, homenetwork);

      const all: HistoryEntry[] = [];

      if (unconfirmed && Array.isArray(unconfirmed)) {
        for (const u of unconfirmed) {
          const entry = u as any;
          all.push({ tx_hash: entry.txId || entry.tx_hash || '', height: 0, satoshis: entry.satoshis });
        }
      }
      if (confirmed && Array.isArray(confirmed)) {
        for (const c of confirmed) {
          const entry = c as any;
          all.push({ tx_hash: entry.txId || entry.tx_hash || '', height: entry.height || 0, satoshis: entry.satoshis });
        }
      }

      cachedHistory.current = all;
      lastFetch.current = Date.now();
      setHistory(all);
    } catch (e) {
      console.error('Failed to fetch history:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const truncateTxid = (txid: string) => {
    if (!txid || txid.length < 16) return txid;
    return `${txid.substring(0, 8)}...${txid.substring(txid.length - 8)}`;
  };

  return (
    <Box>
      <Box sx={WalletStyles.card}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={WalletStyles.sectionTitle}>Histórico de Transações</Typography>
          <Button variant="outlined" size="small" onClick={() => fetchHistory(true)} sx={WalletStyles.outlinedButton}>
            🔄 Atualizar
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} sx={{ color: '#7BA238' }} />
          </Box>
        ) : history.length === 0 ? (
          <Typography sx={WalletStyles.noDataText}>
            Nenhuma transação encontrada.
          </Typography>
        ) : (
          <Box>
            {history.slice(0, 50).map((entry, idx) => (
              <Box key={idx} sx={WalletStyles.listItem}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#7BA238', fontSize: '0.8rem' }}>
                    {truncateTxid(entry.tx_hash)}
                  </Typography>
                  <Chip
                    label={entry.height > 0 ? `Bloco ${entry.height}` : 'Pendente'}
                    size="small"
                    sx={{
                      backgroundColor: entry.height > 0 ? 'rgba(123, 162, 56, 0.15)' : 'rgba(255, 165, 0, 0.15)',
                      color: entry.height > 0 ? '#7BA238' : '#FFA500',
                      fontSize: '0.7rem',
                    }}
                  />
                </Box>
                {entry.satoshis !== undefined && (
                  <Typography variant="body2" sx={{ color: 'rgba(123, 162, 56, 0.6)', fontSize: '0.75rem', mt: 0.5 }}>
                    {entry.satoshis} sats
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default WalletHistory;
